/* eslint-disable no-cond-assign */

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

// Utils
import { ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';

// Types
import Dictionary from '@/types/dictionary';

// Components
import { PercentageCell, StatCell, NotApplicableCell, TextCell, DateCell, CurrencyCell } from '../../../../views/components/cells/index';
import LocalizedString from '@/types/localized-string';

export default class FixedIncomeMetricsDAO extends ComparisonItemBasedProposalDAO {
  labelOverride?: Dictionary<LocalizedString>;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new FixedIncomeMetricsDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const daoConfig: ProposalNS.FixedIncomeMetricsConfig = this.proposalElement.DAO as ProposalNS.FixedIncomeMetricsConfig;

    if (daoConfig.labelOverride !== undefined) {
      this.labelOverride = daoConfig.labelOverride;
    }

    let label: string;
    let field: ProposalFieldDAO;

    const getLabelOverride = (key: string) => {
      const { locale } = this.proposal.options;

      return (this.labelOverride && this.labelOverride[key] && this.labelOverride[key][locale]) || this.formatMessage(key);
    };

    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (daoConfig.metrics.includes(ProposalNS.FixedIncomeMetrics.ALLOCATION)) {
        // Fixed Income Allocation
        const dpResponse: DataPointResponse<number> = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.FIXED_INCOME);
        this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);

        label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.FixedIncomeAllocation');
        const fixedIncomeField: ProposalFieldDAO = new ProposalFieldDAO(label, dpResponse.response.toFixed(2), ProposalStyleClassNames.ProposalTableContent);
        fixedIncomeField.cellType = PercentageCell;
        this.addFieldDAO(itemDAO, fixedIncomeField);
        this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
      }

      const dpResponse2 = DataPoints.getIncomeMetrics(itemDAO.item);
      const { averageCoupon, yieldToMaturity, modifiedDuration, averageQuality, faceValue, couponDates, maturityDate, effectiveDuration, effectiveMaturity } =
        dpResponse2.response;

      this.addDisclaimerKeys(dpResponse2.disclaimerKeys, itemDAO.item);

      for (const metric of daoConfig.metrics) {
        if (metric === ProposalNS.FixedIncomeMetrics.EFFECTIVE_MATURITY) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.effectiveMaturity');
          field = new ProposalFieldDAO(
            label,
            effectiveMaturity !== undefined ? effectiveMaturity : 0,
            ProposalStyleClassNames.ProposalTableContent,
            effectiveMaturity === undefined ? NotApplicableCell : StatCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.EFFECTIVE_DURATION) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.effectiveDuration');
          field = new ProposalFieldDAO(
            label,
            effectiveDuration !== undefined ? effectiveDuration : 0,
            ProposalStyleClassNames.ProposalTableContent,
            effectiveDuration === undefined ? NotApplicableCell : StatCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.AVERAGE_COUPON) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.averageCoupon');
          field = new ProposalFieldDAO(
            label,
            averageCoupon !== undefined ? averageCoupon : 0,
            ProposalStyleClassNames.ProposalTableContent,
            averageCoupon === undefined ? NotApplicableCell : PercentageCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.AVERAGE_QUALITY) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.averageQuality');
          field = new ProposalFieldDAO(
            label,
            averageQuality,
            ProposalStyleClassNames.ProposalTableContent,
            averageQuality === undefined ? NotApplicableCell : TextCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.YIELD_TO_MATURITY) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.yieldToMaturity');
          field = new ProposalFieldDAO(
            label,
            yieldToMaturity !== undefined ? yieldToMaturity : 0,
            ProposalStyleClassNames.ProposalTableContent,
            yieldToMaturity === undefined ? NotApplicableCell : PercentageCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.MODIFIED_DURATION) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.modifiedDuration');
          field = new ProposalFieldDAO(
            label,
            modifiedDuration !== undefined ? modifiedDuration : 0,
            ProposalStyleClassNames.ProposalTableContent,
            modifiedDuration === undefined ? NotApplicableCell : StatCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.FACE_VALUE) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.faceValue');
          field = new ProposalFieldDAO(
            label,
            faceValue !== undefined ? { value: faceValue, currency: itemDAO.item.currency } : undefined,
            ProposalStyleClassNames.ProposalTableContent,
            faceValue === undefined ? NotApplicableCell : CurrencyCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.COUPON_DATES) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.couponDates');
          field = new ProposalFieldDAO(
            label,
            couponDates !== undefined ? couponDates : undefined,
            ProposalStyleClassNames.ProposalTableContent,
            couponDates === undefined ? NotApplicableCell : TextCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }

        if (metric === ProposalNS.FixedIncomeMetrics.MATURITY_DATE) {
          label = getLabelOverride('Comparison.Table.FixedIncomeMetrics.maturityDates');
          field = new ProposalFieldDAO(
            label,
            maturityDate !== undefined ? maturityDate : 0,
            ProposalStyleClassNames.ProposalTableContent,
            maturityDate === undefined ? NotApplicableCell : DateCell,
          );
          this.addFieldDAO(itemDAO, field);
          this.addFieldName(label, ProposalStyleClassNames.ProposalTableContent);
        }
      }
    }
  }
}
